import { Trans } from '@lingui/macro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { memo, useContext, useMemo, useState } from 'react';
import { VList } from 'virtua';

import { IWaitingListItem, UserWhiteListType } from '@/types/key-exchange';

import classes from './P2PAdminPanel.module.css';
import { P2PContext } from './P2PContext';
import P2PListSearch from './P2PListSearch';
import P2PMultipleSelectionActions from './P2PMultipleSelectionActions';
import P2PUsersListItem from './P2PUsersListItem';
import P2PWaitingUsersListItem from './P2PWaitingUsersListItem';

dayjs.extend(utc);

const P2PUsersList: React.FC<{}> = () => {
    const context = useContext(P2PContext);
    const {
        waitingUsersList,
        activeCategory,
        isFetchUsersList,
        usersList,
        searchValue,
    } = context;

    const [selectedUsers, setSelectedUsers] = useState<string[]>([]); // store device_ids only

    const isWaitingCase = activeCategory === 'waiting';
    const isRejectedCase = activeCategory === 'rejected';
    const isPermitsCase = activeCategory === 'permits';

    const showSearch =
        (isWaitingCase && waitingUsersList.length > 0) ||
        (isRejectedCase && usersList.filter(el => el.rejected).length > 0) ||
        (isPermitsCase && usersList.filter(el => !el.rejected).length > 0);

    const filteredList = useMemo(() => {
        let filtered: (UserWhiteListType | IWaitingListItem)[] = usersList;

        if (activeCategory === 'waiting') {
            filtered = waitingUsersList;
        } else if (activeCategory === 'rejected') {
            filtered = usersList.filter(
                (user: UserWhiteListType) => (user.rejected ?? 1) === 1,
            );
        } else {
            filtered = usersList.filter(
                (user: UserWhiteListType) => (user.rejected ?? 1) === 0,
            );
        }

        if (searchValue) {
            const value = searchValue.toLowerCase().trim();

            if (activeCategory === 'waiting') {
                filtered = filtered.filter(
                    user =>
                        (user as IWaitingListItem).user_id
                            .toLowerCase()
                            .includes(value) ||
                        (user as IWaitingListItem).name
                            .toLowerCase()
                            .includes(value),
                );
            } else {
                filtered = filtered.filter(user => {
                    const w = user as UserWhiteListType;
                    const id = (w.id ?? '').toLowerCase();
                    const userId = (w.user_id ?? '').toLowerCase();
                    const name = (w.name ?? '').toLowerCase();
                    const username = (w.username ?? '').toLowerCase();

                    return (
                        id.includes(value) ||
                        userId.includes(value) ||
                        name.includes(value) ||
                        username.includes(value)
                    );
                });
            }
        }

        return filtered;
    }, [activeCategory, usersList, searchValue, waitingUsersList]);

    const waitingCondition = isWaitingCase && filteredList;

    const permitsCondition =
        !isFetchUsersList &&
        isPermitsCase &&
        filteredList &&
        filteredList?.length > 0;

    const rejectedCondition =
        !isFetchUsersList &&
        isRejectedCase &&
        filteredList &&
        filteredList?.length > 0;

    const toggleSelect = (deviceId: string) => {
        setSelectedUsers(prev =>
            prev.includes(deviceId)
                ? prev.filter(id => id !== deviceId)
                : [...prev, deviceId],
        );
    };

    return (
        <div className={`${classes['list-wrap']}`}>
            {waitingCondition && (
                <P2PMultipleSelectionActions
                    selectedUsers={selectedUsers}
                    setSelectedUsers={setSelectedUsers}
                    filteredList={filteredList}
                />
            )}
            {showSearch && <P2PListSearch />}

            <VList
                className={classes['list']}
                style={{
                    marginTop: '8px',
                    height: 'calc(100vh - 150px)',
                }}>
                {searchValue !== '' && filteredList.length === 0 && (
                    <p
                        style={{
                            padding: '12px 24px',
                            textTransform: 'capitalize',
                        }}>
                        <Trans>no search results</Trans>!
                    </p>
                )}
                {permitsCondition &&
                    filteredList.map(
                        (user: UserWhiteListType, index: number) => (
                            <P2PUsersListItem key={index} user={user} />
                        ),
                    )}
                {rejectedCondition &&
                    filteredList.map(
                        (user: UserWhiteListType, index: number) => (
                            <P2PUsersListItem key={index} user={user} />
                        ),
                    )}

                {waitingCondition &&
                    filteredList?.length > 0 &&
                    (filteredList as IWaitingListItem[]).map(
                        (user: IWaitingListItem, index: number) => (
                            <P2PWaitingUsersListItem
                                key={index}
                                user={user}
                                selected={selectedUsers.includes(
                                    user.device_id,
                                )}
                                toggleSelect={toggleSelect}
                            />
                        ),
                    )}
            </VList>
        </div>
    );
};
export default memo(P2PUsersList);
